import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Save, Settings, Package, Search, Building2, ShieldCheck,
  Calendar, MapPin, Mail, Phone, RefreshCw, Truck, Receipt, Plus, Pencil, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'confirmed', label: 'Confirmée', color: 'bg-green-100 text-green-700' },
  { value: 'in_delivery', label: 'En livraison', color: 'bg-blue-100 text-blue-700' },
  { value: 'delivered', label: 'Livrée', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelled', label: 'Annulée', color: 'bg-red-100 text-red-700' },
];

const statusOpt = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

const SETTINGS_FIELDS = [
  { key: 'name', label: 'Raison sociale', placeholder: 'MCB', col: 1 },
  { key: 'brand', label: 'Nom commercial (marque)', placeholder: 'TERRE ET MER LOCATION DE LINGE', col: 1 },
  { key: 'legal_form', label: 'Forme juridique', placeholder: 'SAS', col: 2 },
  { key: 'capital', label: 'Capital social (€)', placeholder: '600', col: 2 },
  { key: 'address_line1', label: 'Adresse', placeholder: '40 BIS RUE DE DINARD', col: 1 },
  { key: 'address_line2', label: "Complément d'adresse", placeholder: '', col: 1 },
  { key: 'postal_code', label: 'Code postal', placeholder: '35730', col: 2 },
  { key: 'city', label: 'Ville', placeholder: 'PLEURTUIT', col: 2 },
  { key: 'country', label: 'Pays', placeholder: 'France', col: 1 },
  { key: 'siret', label: 'SIRET', placeholder: '92981127100014', col: 1 },
  { key: 'rcs', label: 'RCS', placeholder: 'RCS Saint-Malo', col: 1 },
  { key: 'ape', label: 'Code APE / NAF', placeholder: '96.09Z', col: 2 },
  { key: 'vat_number', label: 'N° TVA intracommunautaire', placeholder: 'FR…', col: 2 },
  { key: 'email', label: 'Email de contact', placeholder: 'location@...', col: 1 },
  { key: 'website', label: 'Site web', placeholder: 'terreetmer-location.fr', col: 1 },
  { key: 'iban', label: 'IBAN', placeholder: '', col: 2 },
  { key: 'bic', label: 'BIC', placeholder: '', col: 2 },
];


/* ============================ ORDERS TAB ============================ */

const OrdersTab = ({ token }) => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status_filter=${filter}` : '';
      const res = await axios.get(`${API}/admin/orders${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger les commandes.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, filter, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)),
      );
      toast({ title: 'Statut mis à jour', description: `Commande passée à "${statusOpt(newStatus).label}".` });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Mise à jour impossible.', variant: 'destructive' });
    } finally {
      setUpdating(null);
    }
  };

  const filtered = orders.filter((o) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (o.invoice_number || '').toLowerCase().includes(s) ||
      (o.customer_email || '').toLowerCase().includes(s) ||
      (`${o.customer_first_name || ''} ${o.customer_last_name || ''}`).toLowerCase().includes(s) ||
      (o.delivery_city || '').toLowerCase().includes(s)
    );
  });

  // Stats
  const stats = STATUS_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = orders.filter((o) => o.status === opt.value).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(filter === s.value ? 'all' : s.value)}
            className={`text-left p-4 rounded-xl border transition-all ${
              filter === s.value
                ? 'border-[#E8945A] bg-[#FFF5ED] shadow-sm'
                : 'border-gray-100 bg-white hover:border-gray-200'
            }`}
            data-testid={`stat-${s.value}`}
          >
            <div className="text-xs uppercase tracking-wider text-[#64748B]">{s.label}</div>
            <div className="font-['Playfair_Display'] text-3xl font-medium text-[#2D3E50] mt-1">
              {stats[s.value] || 0}
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par n° facture, client, email, ville..."
            className="pl-10 h-11 bg-white"
            data-testid="admin-orders-search"
          />
        </div>
        <Button
          variant="outline"
          onClick={fetchOrders}
          className="gap-2 h-11"
          data-testid="admin-orders-refresh"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Orders */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#E8945A]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#64748B]">
            Aucune commande {filter !== 'all' ? `avec le statut "${statusOpt(filter).label}"` : ''}.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((o) => (
              <div key={o.id} className="p-5 hover:bg-[#FAFAFA] transition-colors" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Left - meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50]">
                        N° {o.invoice_number || `${o.id.slice(0, 8).toUpperCase()}`}
                      </span>
                      <Badge className={statusOpt(o.status).color}>{statusOpt(o.status).label}</Badge>
                      {o.express_delivery && (
                        <Badge className="bg-amber-100 text-amber-700">Express +40€</Badge>
                      )}
                      {o.billing_same_as_delivery === false && (
                        <Badge className="bg-blue-100 text-blue-700">Facturation distincte</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748B]">
                      <span className="inline-flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        {o.customer_first_name} {o.customer_last_name} · {o.customer_email}
                      </span>
                      {o.customer_phone && (
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" />
                          {o.customer_phone}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748B] mt-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {format(new Date(o.arrival_date), 'dd MMM', { locale: fr })} →{' '}
                        {format(new Date(o.departure_date), 'dd MMM yyyy', { locale: fr })}
                      </span>
                    </div>

                    {/* Addresses block */}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg border border-[#E8945A]/20 bg-[#FFFBF5] p-3">
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#E8945A] font-semibold mb-1">
                          <Truck className="h-3.5 w-3.5" />
                          Livraison
                        </div>
                        <div className="text-sm text-[#2D3E50] leading-snug">
                          {o.delivery_address}
                          <br />
                          {o.delivery_postal_code} {o.delivery_city}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#5493CC]/20 bg-[#F2F8FD] p-3">
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#5493CC] font-semibold mb-1">
                          <Receipt className="h-3.5 w-3.5" />
                          Facturation
                        </div>
                        <div className="text-sm text-[#2D3E50] leading-snug">
                          {o.billing_first_name || o.customer_first_name} {o.billing_last_name || o.customer_last_name}
                          <br />
                          {o.billing_address || o.delivery_address}
                          <br />
                          {o.billing_postal_code || o.delivery_postal_code} {o.billing_city || o.delivery_city}
                        </div>
                      </div>
                    </div>

                    {/* Items detail */}
                    <div className="mt-3 rounded-lg border border-gray-100 overflow-hidden">
                      <div className="bg-[#2D3E50] text-white px-4 py-2 text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
                        <Package className="h-3.5 w-3.5" />
                        Détail des articles à préparer ({o.items.reduce((acc, it) => acc + it.quantity, 0)} {o.items.reduce((acc, it) => acc + it.quantity, 0) > 1 ? 'pièces' : 'pièce'})
                      </div>
                      <div className="divide-y divide-gray-100">
                        {o.items.map((it) => (
                          <div key={it.product_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#E8945A] text-white font-bold text-sm">
                                ×{it.quantity}
                              </span>
                              <span className="font-medium text-[#2D3E50]">{it.product_name}</span>
                            </div>
                            <div className="text-[#64748B]">
                              {(it.price * it.quantity).toFixed(2)} €
                              <span className="text-xs ml-1.5">({it.price.toFixed(2)} €/u)</span>
                            </div>
                          </div>
                        ))}
                        {o.delivery_fee > 0 && (
                          <div className="flex items-center justify-between px-4 py-2 text-sm bg-amber-50">
                            <span className="font-medium text-amber-800">Livraison express</span>
                            <span className="font-semibold text-amber-800">{o.delivery_fee.toFixed(2)} €</span>
                          </div>
                        )}
                        {(o.booking_fee || 0) > 0 && (
                          <div className="flex items-center justify-between px-4 py-2 text-sm bg-gray-50">
                            <span className="font-medium text-[#64748B]">Frais de dossier</span>
                            <span className="font-semibold text-[#2D3E50]">{o.booking_fee.toFixed(2)} €</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {o.notes && (
                      <div className="mt-3 rounded-lg bg-[#FAFAFA] border border-gray-100 p-3 text-sm">
                        <span className="font-semibold text-[#2D3E50]">Note du client :</span>{' '}
                        <span className="text-[#475569]">{o.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Right - amount + status select */}
                  <div className="flex items-center gap-4 lg:flex-col lg:items-end lg:gap-2 lg:min-w-[180px]">
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-wider text-[#64748B]">Total TTC</div>
                      <div className="font-['Playfair_Display'] text-2xl font-medium text-[#E8945A]">
                        {o.total_price.toFixed(2)} €
                      </div>
                    </div>
                    <Select
                      value={o.status}
                      onValueChange={(val) => handleStatusChange(o.id, val)}
                      disabled={updating === o.id}
                    >
                      <SelectTrigger
                        className="w-[170px] h-10"
                        data-testid={`admin-order-status-${o.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ============================ SETTINGS TAB ============================ */

const SettingsTab = ({ token }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setForm(res.data);
      } catch (e) {
        toast({ title: 'Erreur', description: 'Chargement des paramètres impossible.', variant: 'destructive' });
      }
    })();
  }, [token, toast]);

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/settings`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setForm(res.data);
      toast({ title: 'Paramètres enregistrés', description: 'Les nouvelles informations apparaîtront sur les prochaines factures.' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Enregistrement impossible.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  const col1 = SETTINGS_FIELDS.filter((f) => f.col === 1);
  const col2 = SETTINGS_FIELDS.filter((f) => f.col === 2);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[#E8945A]/30 bg-[#FFFBF5] p-5 flex gap-3">
        <ShieldCheck className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#2D3E50]">
          Ces informations apparaissent sur toutes les factures PDF générées par le système. Elles
          sont obligatoires en France (art. L.123-1 du Code de commerce, L.441-9 du Code de commerce).
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Column 1 */}
          <div className="space-y-4">
            {col1.map((f) => (
              <div key={f.key}>
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  value={form[f.key] || ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="mt-1.5 h-11"
                  data-testid={`admin-settings-${f.key}`}
                />
              </div>
            ))}
          </div>
          {/* Column 2 */}
          <div className="space-y-4">
            {col2.map((f) => (
              <div key={f.key}>
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  value={form[f.key] || ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="mt-1.5 h-11"
                  data-testid={`admin-settings-${f.key}`}
                />
              </div>
            ))}
            {/* VAT toggle */}
            <div className="rounded-lg border border-gray-100 bg-[#FAFAFA] p-4 flex items-start gap-3">
              <Switch
                id="vat_exempt"
                checked={!!form.vat_exempt}
                onCheckedChange={(v) => handleChange('vat_exempt', v)}
                data-testid="admin-settings-vat_exempt"
              />
              <div className="flex-1">
                <Label htmlFor="vat_exempt" className="cursor-pointer font-medium">
                  Franchise en base de TVA (auto-entrepreneur / micro)
                </Label>
                <p className="text-xs text-[#64748B] mt-1">
                  Si activé, la mention « TVA non applicable, art. 293 B du CGI » apparaît sur les factures.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-end gap-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-11 px-6"
            data-testid="admin-settings-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};


/* ============================ PRODUCTS TAB ============================ */

const CATEGORY_OPTIONS = [
  { value: 'kits-couette', label: 'Kits Draps' },
  { value: 'linge-de-toilette', label: 'Linge de toilette' },
  { value: 'check-in-out', label: 'Check-in / Check-out' },
];

const categoryLabel = (v) =>
  CATEGORY_OPTIONS.find((o) => o.value === v)?.label || v;

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  category: 'kits-couette',
  available: true,
};

const ProductsTab = ({ token }) => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | product
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger les produits.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreate = () => {
    setForm(emptyProduct);
    setEditing({});
  };

  const openEdit = (product) => {
    setForm({
      name: product.name,
      description: product.description,
      price: String(product.price),
      category: product.category,
      available: product.available !== false,
    });
    setEditing(product);
  };

  const closeDialog = () => {
    setEditing(null);
    setForm(emptyProduct);
  };

  const handleSave = async () => {
    const priceNum = parseFloat(form.price);
    if (!form.name.trim() || !form.description.trim() || isNaN(priceNum) || priceNum < 0) {
      toast({ title: 'Champs invalides', description: 'Nom, description et prix (≥0) requis.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        price: priceNum,
        category: form.category,
        available: !!form.available,
      };
      if (editing && editing.id) {
        await axios.patch(`${API}/admin/products/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: 'Produit mis à jour' });
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: 'Produit créé' });
      }
      closeDialog();
      fetchProducts();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/products/${confirmDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Produit supprimé' });
      setConfirmDelete(null);
      fetchProducts();
    } catch (e) {
      toast({ title: 'Erreur', description: 'Suppression impossible.', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  // Group by category for clearer display
  const grouped = CATEGORY_OPTIONS.map((cat) => ({
    ...cat,
    items: products.filter((p) => p.category === cat.value),
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl font-medium text-[#2D3E50]">
            Catalogue produits
          </h2>
          <p className="text-sm text-[#64748B] mt-1">
            {products.length} produit{products.length > 1 ? 's' : ''} — modifications visibles immédiatement côté client.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchProducts} className="gap-2 h-10" data-testid="admin-products-refresh">
            <RefreshCw className="h-4 w-4" />
            Rafraîchir
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-10"
            data-testid="admin-products-new"
          >
            <Plus className="h-4 w-4" />
            Nouveau produit
          </Button>
        </div>
      </div>

      {grouped.map((cat) => (
        <div
          key={cat.value}
          className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          data-testid={`admin-products-cat-${cat.value}`}
        >
          <div className="px-5 py-3 bg-[#FAFAFA] border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-medium text-[#2D3E50]">
              {cat.label}
              <span className="ml-2 text-sm text-[#64748B] font-normal">
                ({cat.items.length})
              </span>
            </h3>
          </div>
          {cat.items.length === 0 ? (
            <p className="text-sm text-[#64748B] px-5 py-6 text-center">
              Aucun produit dans cette catégorie.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {cat.items.map((p) => (
                <div
                  key={p.id}
                  className="px-5 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3"
                  data-testid={`admin-product-row-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#2D3E50]">{p.name}</span>
                      {!p.available && (
                        <Badge variant="outline" className="text-xs border-gray-300 text-[#64748B]">
                          Indisponible
                        </Badge>
                      )}
                      <span className="text-xs text-[#94A3B8] font-mono">{p.id}</span>
                    </div>
                    <p className="text-sm text-[#64748B] mt-1 line-clamp-2 whitespace-pre-line">
                      {p.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-['Playfair_Display'] text-xl font-semibold text-[#E8945A] min-w-[70px] text-right">
                      {p.price.toFixed(2)} €
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => openEdit(p)}
                        data-testid={`admin-product-edit-${p.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setConfirmDelete(p)}
                        data-testid={`admin-product-delete-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing && editing.id ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
            <DialogDescription>
              {editing && editing.id
                ? 'Les modifications sont prises en compte immédiatement sur le site.'
                : 'Ajoutez un nouvel article au catalogue.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="prod-name">Nom *</Label>
              <Input
                id="prod-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Kit Draps S"
                className="mt-1.5 h-11"
                data-testid="admin-product-form-name"
              />
            </div>

            <div>
              <Label htmlFor="prod-description">Description *</Label>
              <Textarea
                id="prod-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={"Pour un lit 1 place en 90 cm\n• 1 drap plat\n• 1 housse de couette\n• 1 taie d'oreiller"}
                rows={5}
                className="mt-1.5"
                data-testid="admin-product-form-description"
              />
              <p className="text-xs text-[#64748B] mt-1">
                Les retours à la ligne et puces (•) sont préservés sur la fiche produit.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prod-price">Prix (€) *</Label>
                <Input
                  id="prod-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="20.00"
                  className="mt-1.5 h-11"
                  data-testid="admin-product-form-price"
                />
              </div>
              <div>
                <Label htmlFor="prod-category">Catégorie *</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger id="prod-category" className="mt-1.5 h-11" data-testid="admin-product-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border border-gray-100 bg-[#FAFAFA] p-4 flex items-start gap-3">
              <Switch
                id="prod-available"
                checked={!!form.available}
                onCheckedChange={(v) => setForm({ ...form, available: v })}
                data-testid="admin-product-form-available"
              />
              <div className="flex-1">
                <Label htmlFor="prod-available" className="cursor-pointer font-medium">
                  Disponible à la vente
                </Label>
                <p className="text-xs text-[#64748B] mt-1">
                  Si désactivé, le produit reste en base mais n'apparaît plus dans le catalogue public.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={saving}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#E8945A] hover:bg-[#D4844A] gap-2"
              data-testid="admin-product-form-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editing && editing.id ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.name}</strong> sera définitivement retiré du catalogue.
              Les commandes existantes conservent leur copie du produit (nom + prix).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-product-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


/* ============================ MAIN PAGE ============================ */

const AdminPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading) {
      if (!user) navigate('/login?redirect=/admin');
      else if (!user.is_admin) navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 bg-[#FAFAFA]" data-testid="admin-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium mb-3">
            <ShieldCheck className="h-4 w-4" />
            Espace administrateur
          </span>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl tracking-tight font-medium">
            Tableau de bord admin
          </h1>
          <p className="text-white/70 mt-2">
            Gérez vos commandes et les informations légales de votre société.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Tabs defaultValue="orders">
          <TabsList className="bg-white border border-gray-100 p-1 rounded-xl">
            <TabsTrigger value="orders" className="gap-2 data-[state=active]:bg-[#FFF5ED] data-[state=active]:text-[#E8945A]" data-testid="tab-orders">
              <Package className="h-4 w-4" />
              Commandes
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2 data-[state=active]:bg-[#FFF5ED] data-[state=active]:text-[#E8945A]" data-testid="tab-products">
              <Receipt className="h-4 w-4" />
              Produits
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 data-[state=active]:bg-[#FFF5ED] data-[state=active]:text-[#E8945A]" data-testid="tab-settings">
              <Building2 className="h-4 w-4" />
              Société (facturation)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            <OrdersTab token={token} />
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            <ProductsTab token={token} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
